import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { pointsConfigApi, type PointsConfigurationInput } from "../services/pointsConfigApi";

const listKey = (status?: string) => ["points-config", "list", status ?? "all"] as const;
const detailKey = (id: string) => ["points-config", "detail", id] as const;

const invalidate = () => queryClient.invalidateQueries({ queryKey: ["points-config"] });

export const usePointsConfigurations = (status?: string) =>
  useQuery({
    queryKey: listKey(status),
    queryFn: () => pointsConfigApi.getAll(status),
  });

export const usePointsConfiguration = (id: string | undefined) =>
  useQuery({
    queryKey: id ? detailKey(id) : ["points-config", "detail", "none"],
    queryFn: () => pointsConfigApi.getById(id!),
    enabled: !!id,
  });

export const useCreatePointsConfiguration = () =>
  useMutation({
    mutationFn: (payload: PointsConfigurationInput) => pointsConfigApi.create(payload),
    onSuccess: invalidate,
  });

export const useUpdatePointsConfiguration = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PointsConfigurationInput }) =>
      pointsConfigApi.update(id, payload),
    onSuccess: invalidate,
  });

export const useSubmitPointsConfiguration = () =>
  useMutation({ mutationFn: (id: string) => pointsConfigApi.submit(id), onSuccess: invalidate });

export const useApprovePointsConfiguration = () =>
  useMutation({ mutationFn: (id: string) => pointsConfigApi.approve(id), onSuccess: invalidate });

export const useDeactivatePointsConfiguration = () =>
  useMutation({ mutationFn: (id: string) => pointsConfigApi.deactivate(id), onSuccess: invalidate });

export const useRejectPointsConfiguration = () =>
  useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      pointsConfigApi.reject(id, rejectionReason),
    onSuccess: invalidate,
  });
